import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Box,
  TextField,
} from '@mui/material';
import { ModelConfig, ModelField } from './RbacConfig';
import { AdminFormField } from './AdminFormFields';
import JsonViewEditor from '@uiw/react-json-view/editor';

type Props = {
  open: boolean;
  model: ModelConfig;
  fields: ModelField[];
  data: { [key: string]: any };
  setData: (d: any) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  selectOptions: { [endpoint: string]: any[] };
  isLoading?: boolean;
  multiSelectRenderer?: (
    field: ModelField & { isMultiple?: boolean },
    value: any,
    onChange: (newValue: any) => void,
  ) => React.ReactNode;

  /** NEW: read-only mode (View) */
  readOnly?: boolean;
};

export function AdminDialog({
                              open,
                              model,
                              fields,
                              data,
                              setData,
                              onSave,
                              onCancel,
                              isSaving,
                              selectOptions,
                              isLoading,
                              multiSelectRenderer,
                              readOnly = false,
                            }: Props) {
  const isEdit = !!(data as any)?.id;
  const titleVerb = readOnly ? 'View' : isEdit ? 'Edit' : 'Add';

  const disabledAll = isSaving || readOnly;

  const handleSalesLevelChange = (levelKey: string, newValue: string) => {
    // In read-only mode, do nothing.
    if (readOnly) return;

    const match = levelKey.match(/^sales_level_(\d)$/);
    if (!match) {
      setData({ ...data, [levelKey]: newValue });
      return;
    }
    const level = Number(match[1]);
    const newData = { ...data, [levelKey]: newValue };
    for (let i = level + 1; i <= 6; i++) {
      newData[`sales_level_${i}`] = '';
    }
    setData(newData);
  };

  // Registry JSON Modal state
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [editRegistry, setEditRegistry] = useState<object>({});

  const handleOpenJsonModal = () => {
    let registryObj = {};
    try {
      registryObj = data.registry
        ? typeof data.registry === 'string'
          ? JSON.parse(data.registry)
          : data.registry
        : {};
    } catch {
      registryObj = {};
    }
    setEditRegistry(registryObj);
    setJsonModalOpen(true);
  };

  const handleSaveJsonModal = (newJson: object) => {
    if (readOnly) {
      setJsonModalOpen(false);
      return;
    }
    setData({ ...data, registry: JSON.stringify(newJson) });
    setJsonModalOpen(false);
  };

  const handleRegistryChange = (value: unknown) => {
    if (readOnly) return;
    if (value && typeof value === 'object') {
      setEditRegistry(value as object);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {titleVerb} {model.label}
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <CircularProgress />
        ) : (
          fields.map((field) => {
            const fieldWithMeta = field as ModelField & { isMultiple?: boolean };

            // Multi-select (custom renderer)
            if (field.type === 'select' && fieldWithMeta.isMultiple && multiSelectRenderer) {
              return (
                <Box key={field.key}>
                  {multiSelectRenderer(fieldWithMeta, data[field.key], (newValue) => {
                    if (readOnly) return;
                    setData({ ...data, [field.key]: newValue });
                  })}
                </Box>
              );
            }

            // Boolean checkbox
            if (field.type === 'boolean') {
              return (
                <FormControlLabel
                  key={field.key}
                  control={
                    <Checkbox
                      checked={!!data[field.key]}
                      onChange={(e) => {
                        if (readOnly) return;
                        setData({ ...data, [field.key]: e.target.checked });
                      }}
                      disabled={disabledAll}
                    />
                  }
                  label={field.label || field.key}
                />
              );
            }

            // sales_level_* cascading behavior
            if (field.key.startsWith('sales_level_')) {
              return (
                <AdminFormField
                  key={field.key}
                  field={field}
                  value={data[field.key]}
                  onChange={(val) => handleSalesLevelChange(field.key, val)}
                  selectOptions={selectOptions}
                  disabled={disabledAll}
                  data={data}
                />
              );
            }

            // registry JSON editor modal
            if (field.key === 'registry') {
              return (
                <Box key="registry" display="flex" alignItems="center" gap={2}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label={field.label}
                    value={typeof data.registry === 'string' ? data.registry : JSON.stringify(data.registry)}
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                  <Button variant="outlined" size="small" onClick={handleOpenJsonModal}>
                    {readOnly ? 'View JSON' : 'Edit JSON'}
                  </Button>

                  <Dialog open={jsonModalOpen} onClose={() => setJsonModalOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>{readOnly ? 'View Registry JSON' : 'Edit Registry JSON'}</DialogTitle>
                    <DialogContent>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <JsonViewEditor
                          value={editRegistry}
                          onChange={handleRegistryChange}
                          style={{ height: 260 }}
                          // JsonViewEditor does not have a universal "readOnly" prop;
                          // we enforce readOnly by ignoring onChange above.
                        />
                      </Box>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setJsonModalOpen(false)}>Close</Button>
                      {!readOnly && (
                        <Button variant="contained" onClick={() => handleSaveJsonModal(editRegistry)}>
                          Save
                        </Button>
                      )}
                    </DialogActions>
                  </Dialog>
                </Box>
              );
            }

            // Default field renderer
            return (
              <AdminFormField
                key={field.key}
                field={field}
                value={data[field.key]}
                onChange={(val) => {
                  if (readOnly) return;
                  setData({ ...data, [field.key]: val });
                }}
                selectOptions={selectOptions}
                disabled={disabledAll}
                data={data}
              />
            );
          })
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={isSaving}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && (
          <Button onClick={onSave} variant="contained" disabled={isSaving}>
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}